import React from "react";
import { Icon, notify } from "./registry";
import { FLAKE_BLENDS, FINISHES, blendOf, finishOf, visualLabel, specks, homography, applyH, orderCorners, defaultQuad, unitCells, triangleAffine, DEFAULT_BLEND, DEFAULT_FINISH } from "./lib/flakes";
import { uploadPhoto, uploadsConfigured } from "./lib/media";
import { newId } from "./lib/ids";
/* ============================================================
   Fence Works — the coating visualizer (docs/SERVICE_SWITCHER_PLAN.md
   §13 item 14). Client-side, no render wait, no credits: the flake floor is
   drawn onto the customer's own garage photo.

   The owner taps the four corners of the floor; a flake tile (lib/flakes.ts
   — the blend's base coat and speckle field, deterministic) is laid onto
   that quad through a homography, the photo's own shading comes back over
   it (a luminosity pass keeps the light, the shadows and the objects on the
   floor), and the finish reads as a sheen. A slider compares before and
   after. "Save to the quote" uploads the picture (lib/media.ts) and keeps
   it on q.visuals — never in wo.photos, which feeds the website gallery and
   the social posts — and the hub shows it under "Your floor, previewed".
   ============================================================ */
type Pt = [number, number];
const TEX = 1024;
const tiles = new Map<string, HTMLCanvasElement>();

/* the flake tile: the base coat, a little grain, the speckle field */
function tileFor(blendId: string, system: string, density: number, seed: number): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  const key = [blendId, system, density.toFixed(2), seed].join("|");
  const hit = tiles.get(key);
  if (hit) return hit;
  const blend = blendOf(blendId);
  const c = document.createElement("canvas"); c.width = TEX; c.height = TEX;
  const ctx = c.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = blend.base; ctx.fillRect(0, 0, TEX, TEX);
  // a faint grain so a solid colour doesn't read as a flat paint bucket
  const g = specks(seed + 101, TEX, TEX, { ...blend, flakes: ["#ffffff", "#000000"] }, { density: 0.35 });
  g.forEach((s) => { ctx.globalAlpha = 0.05; ctx.fillStyle = s.color; ctx.fillRect(s.x, s.y, 2, 2); });
  ctx.globalAlpha = 1;
  specks(seed, TEX, TEX, blend, { density, system }).forEach((s) => {
    ctx.save(); ctx.translate(s.x, s.y); ctx.rotate(s.rot); ctx.fillStyle = s.color;
    ctx.beginPath(); ctx.moveTo(-s.r, -s.r * 0.6); ctx.lineTo(s.r * 0.9, -s.r * 0.8); ctx.lineTo(s.r, s.r * 0.5); ctx.lineTo(-s.r * 0.7, s.r * 0.9); ctx.closePath(); ctx.fill();
    ctx.restore();
  });
  if (tiles.size > 24) tiles.delete(tiles.keys().next().value as string);
  tiles.set(key, c);
  return c;
}
function drawTri(ctx: CanvasRenderingContext2D, tile: HTMLCanvasElement, s: [Pt, Pt, Pt], d: [Pt, Pt, Pt]) {
  const T = triangleAffine(s, d);
  if (!T) return;
  ctx.save();
  ctx.beginPath(); ctx.moveTo(d[0][0], d[0][1]); ctx.lineTo(d[1][0], d[1][1]); ctx.lineTo(d[2][0], d[2][1]); ctx.closePath();
  ctx.clip();
  ctx.transform(T[0], T[1], T[2], T[3], T[4], T[5]);
  ctx.drawImage(tile, 0, 0);
  ctx.restore();
}
/* the whole picture: the photo, the floor, the photo's shading back, the sheen */
export function renderPreview(ctx: CanvasRenderingContext2D, img: CanvasImageSource, W: number, H: number, corners: Pt[], opts: { blend: string; finish: string; system: string; density: number; seed: number; opacity?: number }) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = "source-over"; ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, W, H);
  ctx.drawImage(img, 0, 0, W, H);
  const quad = orderCorners(corners);
  const Hm = homography([[0, 0], [1, 0], [1, 1], [0, 1]], quad);
  const tile = tileFor(opts.blend, opts.system, opts.density, opts.seed);
  if (!Hm || !tile) return;
  ctx.save();
  ctx.beginPath(); quad.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath(); ctx.clip();
  ctx.globalAlpha = opts.opacity == null ? 0.96 : opts.opacity;
  unitCells(18).forEach((c) => {
    const p00 = applyH(Hm, c.u0, c.v0), p10 = applyH(Hm, c.u1, c.v0), p11 = applyH(Hm, c.u1, c.v1), p01 = applyH(Hm, c.u0, c.v1);
    const t00: Pt = [c.u0 * TEX, c.v0 * TEX], t10: Pt = [c.u1 * TEX, c.v0 * TEX], t11: Pt = [c.u1 * TEX, c.v1 * TEX], t01: Pt = [c.u0 * TEX, c.v1 * TEX];
    // a hair of overlap so the triangles don't leave seams
    const grow = (a: Pt, b: Pt, cc: Pt): [Pt, Pt, Pt] => { const cx = (a[0] + b[0] + cc[0]) / 3, cy = (a[1] + b[1] + cc[1]) / 3; const g = (p: Pt): Pt => [p[0] + Math.sign(p[0] - cx) * 0.6, p[1] + Math.sign(p[1] - cy) * 0.6]; return [g(a), g(b), g(cc)]; };
    drawTri(ctx, tile, [t00, t10, t11], grow(p00, p10, p11));
    drawTri(ctx, tile, [t00, t11, t01], grow(p00, p11, p01));
  });
  // the photo's own light and shadows, the flake's colour
  ctx.globalCompositeOperation = "luminosity"; ctx.globalAlpha = 0.55;
  ctx.drawImage(img, 0, 0, W, H);
  // the finish: a sheen falling across the floor from its far edge
  const sheen = finishOf(opts.finish).sheen;
  if (sheen > 0) {
    const ys = quad.map((p) => p[1]); const y0 = Math.min(...ys), y1 = Math.max(...ys);
    const gr = ctx.createLinearGradient(0, y0, 0, y1);
    gr.addColorStop(0, "rgba(255,255,255," + sheen.toFixed(2) + ")"); gr.addColorStop(0.55, "rgba(255,255,255," + (sheen * 0.35).toFixed(2) + ")"); gr.addColorStop(1, "rgba(255,255,255,0)");
    ctx.globalCompositeOperation = "screen"; ctx.globalAlpha = 1; ctx.fillStyle = gr; ctx.fillRect(0, y0, W, y1 - y0);
  }
  ctx.restore();
  ctx.globalCompositeOperation = "source-over"; ctx.globalAlpha = 1;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    if (/^https?:/i.test(src)) im.crossOrigin = "anonymous";   // so the canvas can be saved
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("Couldn't load that photo."));
    im.src = src;
  });
}
const dataUrlOf = (file: File, maxPx: number, q: number): Promise<string> => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file); const im = new Image();
  im.onload = () => { const s = Math.min(maxPx / im.naturalWidth, maxPx / im.naturalHeight, 1); const c = document.createElement("canvas"); c.width = Math.max(1, Math.round(im.naturalWidth * s)); c.height = Math.max(1, Math.round(im.naturalHeight * s)); c.getContext("2d")!.drawImage(im, 0, 0, c.width, c.height); URL.revokeObjectURL(url); resolve(c.toDataURL("image/jpeg", q)); };
  im.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Couldn't read that file.")); };
  im.src = url;
});

export type Visual = { id: string; at: number; kind?: "preview"; label: string; blend: string; finish: string; system: string; url: string; before?: string; w?: number; h?: number; pending?: boolean };   // kind: lib/visuals.ts — the fence studio's renders share q.visuals (§13 item 27)

/* The modal. `photos` are candidates to start from (the request's hosted
   photos); the owner can also pick one from this device. onSave gets the
   Visual (and the blend / finish / system it was drawn with). */
export function CoatingPreview({ photos, initial, onSave, onClose }: { photos?: string[]; initial?: { blend?: string; finish?: string; system?: string }; onSave: (v: Visual) => void; onClose: () => void }) {
  const [src, setSrc] = React.useState<string>((photos || [])[0] || "");
  const [file, setFile] = React.useState<File | null>(null);
  const [img, setImg] = React.useState<HTMLImageElement | null>(null);
  const [err, setErr] = React.useState<string>("");
  const [blend, setBlend] = React.useState<string>((initial && initial.blend) || DEFAULT_BLEND);
  const [finish, setFinish] = React.useState<string>((initial && initial.finish) || DEFAULT_FINISH);
  const [system, setSystem] = React.useState<string>((initial && initial.system) || "full-flake");
  const [density, setDensity] = React.useState<number>(1);
  const [corners, setCorners] = React.useState<Pt[]>([]);
  const [compare, setCompare] = React.useState<number>(100);
  const [busy, setBusy] = React.useState<boolean>(false);
  const [seed] = React.useState<number>(() => Math.floor(Math.random() * 1e6));
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const dragRef = React.useRef<number | null>(null);
  const W = img ? Math.min(1400, img.naturalWidth) : 0;
  const H = img ? Math.round((img.naturalHeight / (img.naturalWidth || 1)) * W) : 0;

  React.useEffect(() => {
    let dead = false;
    if (!src) { setImg(null); return; }
    setErr("");
    loadImage(src).then((im) => { if (dead) return; setImg(im); const w = Math.min(1400, im.naturalWidth), h = Math.round((im.naturalHeight / (im.naturalWidth || 1)) * w); setCorners(defaultQuad(w, h)); })
      .catch((e: any) => { if (!dead) { setImg(null); setErr(String((e && e.message) || e)); } });
    return () => { dead = true; };
  }, [src]);
  React.useEffect(() => {
    const c = canvasRef.current, ctx = c && c.getContext("2d");
    if (!c || !ctx || !img || corners.length !== 4) return;
    c.width = W; c.height = H;
    try { renderPreview(ctx, img, W, H, corners, { blend, finish, system, density, seed }); } catch (e) { /* a tainted or odd photo: the before stays */ }
  }, [img, corners, blend, finish, system, density, W, H, seed]);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files && e.target.files[0]; e.target.value = "";
    if (!f) return;
    try { setFile(f); setSrc(await dataUrlOf(f, 1600, 0.86)); } catch (er: any) { setErr(String((er && er.message) || er)); }
  }
  // corner handles: pointer → image coordinates through the stage's scale
  function toImage(ev: { clientX: number; clientY: number }): Pt {
    const el = stageRef.current; if (!el) return [0, 0];
    const r = el.getBoundingClientRect();
    return [Math.max(0, Math.min(W, ((ev.clientX - r.left) / r.width) * W)), Math.max(0, Math.min(H, ((ev.clientY - r.top) / r.height) * H))];
  }
  function onDown(i: number, ev: React.PointerEvent) { dragRef.current = i; (ev.target as any).setPointerCapture && (ev.target as any).setPointerCapture(ev.pointerId); ev.preventDefault(); }
  function onMove(ev: React.PointerEvent) { const i = dragRef.current; if (i == null) return; const p = toImage(ev); setCorners((cs) => cs.map((c, k) => (k === i ? p : c))); }
  function onUp() { dragRef.current = null; }

  async function save() {
    const c = canvasRef.current; if (!c || !img) return;
    setBusy(true);
    try {
      const blob: Blob | null = await new Promise((res) => c.toBlob((b) => res(b), "image/jpeg", 0.86));
      if (!blob) throw new Error("Couldn't save the picture from this photo (it may be from another site). Add it from this device instead.");
      const after = await uploadPhoto(new File([blob], "floor-preview.jpg", { type: "image/jpeg" }));
      let before: string | undefined = /^https?:/i.test(src) ? src : undefined;
      if (!before && file && uploadsConfigured()) { try { const up = await uploadPhoto(file); if (/^https?:/i.test(up.url)) before = up.url; } catch (e) { /* the after alone is fine */ } }
      // offline (or a timed-out upload) the render is a data URL flagged pending: lib/media.ts
      // startMediaSync uploads it later and rewrites the quote, like a job photo
      const v: Visual = { id: newId("vis"), at: Date.now(), kind: "preview", label: visualLabel(blend, finish, system), blend, finish, system, url: after.url, before, w: W, h: H, ...(after.pending ? { pending: true } : {}) };
      onSave(v);
      notify(after.pending ? "Saved on this device — it uploads when there's signal." : "Saved to the quote.");
      onClose();
    } catch (e: any) { notify(String((e && e.message) || e), { kind: "error" }); }
    setBusy(false);
  }

  const candidates = (photos || []).filter((p) => typeof p === "string" && p);
  return (
    <div className="cp-backdrop" onClick={onClose}>
      <div className="cp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Coating preview">
        <div className="row between" style={{ marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
          <div><div className="kicker kicker-teal"><Icon name="edit" size={13} /> Preview the floor</div><p className="muted" style={{ margin: "2px 0 0", fontSize: 12.5 }}>Drag the four dots to the corners of the floor; pick the blend and the finish. Nothing is sent anywhere — it's drawn here.</p></div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <div className="cp-layout">
          <div>
            {!img && (
              <div className="cp-empty">
                {err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: "0 0 8px" }}>{err}</p>}
                <p className="muted" style={{ fontSize: 13, margin: "0 0 10px" }}>Start from the customer's garage photo.</p>
                <div className="row gap-8" style={{ flexWrap: "wrap", justifyContent: "center" }}>
                  {candidates.slice(0, 6).map((u, i) => <button key={i} className="cp-thumb" onClick={() => setSrc(u)} style={{ backgroundImage: `url(${u})` }} title="Use this photo" />)}
                  <label className="btn btn-outline btn-sm" style={{ cursor: "pointer" }}><Icon name="plus" size={13} />Photo from this device<input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={pick} /></label>
                </div>
              </div>
            )}
            {img && (
              <div className="cp-stage" ref={stageRef} style={{ aspectRatio: `${W} / ${H}` }} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
                <img src={src} alt="" className="cp-before" draggable={false} />
                <canvas ref={canvasRef} className="cp-after" style={{ clipPath: `inset(0 ${100 - compare}% 0 0)` }} />
                {compare > 0 && compare < 100 && <div className="cp-divider" style={{ left: compare + "%" }} />}
                {corners.map((p, i) => <button key={i} className="cp-handle" style={{ left: (p[0] / W) * 100 + "%", top: (p[1] / H) * 100 + "%" }} onPointerDown={(e) => onDown(i, e)} aria-label={"Floor corner " + (i + 1)} />)}
              </div>
            )}
            {img && (
              <div className="row gap-8" style={{ alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <span className="muted" style={{ fontSize: 12 }}>Before</span>
                <input type="range" min={0} max={100} value={compare} onChange={(e) => setCompare(Number(e.target.value))} style={{ flex: "1 1 160px" }} aria-label="Before and after" />
                <span className="muted" style={{ fontSize: 12 }}>After</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setCorners(defaultQuad(W, H))}>Reset corners</button>
                <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>Other photo<input type="file" accept="image/*" style={{ display: "none" }} onChange={pick} /></label>
              </div>
            )}
          </div>
          <div className="cp-side">
            <div className="field-label">Flake blend</div>
            <div className="cp-swatches">
              {FLAKE_BLENDS.map((b) => (
                <button key={b.id} className={"cp-swatch" + (blend === b.id ? " on" : "")} onClick={() => setBlend(b.id)} title={b.name} aria-pressed={blend === b.id}>
                  <span className="cp-swatch-chip" style={{ background: `radial-gradient(circle at 30% 30%, ${b.flakes[3]} 0 12%, transparent 13%), radial-gradient(circle at 70% 60%, ${b.flakes[0]} 0 14%, transparent 15%), radial-gradient(circle at 45% 75%, ${b.flakes[1]} 0 10%, transparent 11%), ${b.base}` }} />
                  <span>{b.name}</span>
                </button>
              ))}
            </div>
            <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 10 }}>
              <div><label className="field-label" htmlFor="cp-system">Broadcast</label>
                <select id="cp-system" className="select" value={system} onChange={(e) => setSystem(e.target.value)}><option value="full-flake">Full flake</option><option value="partial-flake">Partial flake</option><option value="solid">Solid colour</option></select></div>
              <div><label className="field-label" htmlFor="cp-finish">Finish</label>
                <select id="cp-finish" className="select" value={finish} onChange={(e) => setFinish(e.target.value)}>{FINISHES.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
            </div>
            {system !== "solid" && <div style={{ marginTop: 10 }}><label className="field-label" htmlFor="cp-density">Flake density</label>
              <input id="cp-density" type="range" min={0.5} max={1.5} step={0.05} value={density} onChange={(e) => setDensity(Number(e.target.value))} style={{ width: "100%" }} /></div>}
            <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>{visualLabel(blend, finish, system)}. The real floor's colour depends on the lot and the topcoat — this is a preview, not a sample chip.</p>
            <div className="row gap-8" style={{ marginTop: 12, flexWrap: "wrap" }}>
              <button className="btn btn-primary" disabled={!img || busy} onClick={save}><Icon name="check" size={14} />{busy ? "Saving…" : "Save to the quote"}</button>
              <button className="btn btn-ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* On the quote page: the saved previews, and a new one. */
export function CoatingPreviewCard({ q, store, canEdit = true }: { q: any; store: any; canEdit?: boolean }) {
  const [open, setOpen] = React.useState<boolean>(false);
  const visuals: Visual[] = Array.isArray(q && q.visuals) ? q.visuals : [];
  const coat = (q && q.inputs && q.inputs.concrete && Array.isArray(q.inputs.concrete.blocks) ? q.inputs.concrete.blocks : []).find((b: any) => b && b.jobType === "coat") || {};
  const photos = q && q.inputs && q.inputs.concrete && Array.isArray(q.inputs.concrete.photos) ? q.inputs.concrete.photos.filter((p: any) => typeof p === "string") : [];
  const save = (v: Visual) => store.updateQuote(q.id, { visuals: [...visuals, v] });
  const remove = (id: string) => store.updateQuote(q.id, { visuals: visuals.filter((v) => v.id !== id) });
  return (
    <div className="card pad" style={{ marginTop: 12 }} data-tour="coat-previews">
      <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginBottom: visuals.length ? 10 : 0 }}>
        <div><div className="kicker kicker-teal"><Icon name="edit" size={13} /> Their floor, previewed</div><p className="muted" style={{ margin: "2px 0 0", fontSize: 12.5 }}>The flake blend on the customer's own garage photo. Saved previews show on their page.</p></div>
        {canEdit && <button className="btn btn-outline btn-sm" onClick={() => setOpen(true)}><Icon name="plus" size={13} />New preview</button>}
      </div>
      {visuals.length > 0 && (
        <div className="cp-grid">
          {visuals.map((v) => (
            <figure key={v.id} className="cp-fig">
              <a href={v.url} target="_blank" rel="noopener"><img src={v.url} alt={v.label} /></a>
              <figcaption className="row between" style={{ gap: 6 }}><span>{v.label}</span>{canEdit && <button className="proj-del" style={{ background: "none", border: 0, color: "var(--fg-3)", cursor: "pointer" }} aria-label="Remove preview" onClick={() => remove(v.id)}><Icon name="trash" size={13} /></button>}</figcaption>
            </figure>
          ))}
        </div>
      )}
      {open && <CoatingPreview photos={photos} initial={{ blend: coat.flake, finish: coat.finish, system: coat.system }} onSave={save} onClose={() => setOpen(false)} />}
    </div>
  );
}
